import { useMemo } from 'react';
import type { AccountContact } from '@/api/commercial/accounts';
import type { ProposalCreditAnalysisResolution } from '@/api/commercial/proposals';
import { ProposalStatus } from '@/api/commercial/proposals';
import { type IProposalCartItem, NegotiationType } from '@/types/commercial/cart';

export interface ValidationMessage {
  code: string;
  message: string;
}

export interface ResumeValidation {
  canSubmit: boolean;
  blockers: ValidationMessage[];
}

interface ResumeValidationInput {
  proposalStatus?: string;
  hasCartItems: boolean;
  hasPayment: boolean;
  hasDeliveryAddress: boolean;
  hasFiscalContext?: boolean;
  cartItems: IProposalCartItem[];
  creditAnalysisResolution: ProposalCreditAnalysisResolution | null;
}

export function hasContractRequiringItems(items: IProposalCartItem[]): boolean {
  return items.some(
    (item) =>
      item.negotiationType === NegotiationType.SALE_WITH_SIM_CARD ||
      item.negotiationType === NegotiationType.SIM_CARD ||
      (item.negotiationType === NegotiationType.SERVICES && item.unitPriceMonthlyInCents > 0),
  );
}

export function hasContractEnabledContact(contacts: AccountContact[]): boolean {
  return contacts.some((c) => c.contractEnabled);
}

export function useResumeValidation(input: ResumeValidationInput): ResumeValidation {
  return useMemo(() => {
    const blockers: ValidationMessage[] = [];

    if (input.proposalStatus !== ProposalStatus.OPEN) {
      return { canSubmit: false, blockers: [] };
    }

    if (!input.hasCartItems) {
      blockers.push({
        code: 'NO_ITEMS',
        message: 'Adicione pelo menos um item ao carrinho',
      });
    }

    if (!input.hasPayment) {
      blockers.push({
        code: 'NO_PAYMENT',
        message: 'Configure as condições de pagamento',
      });
    }

    if (!input.hasDeliveryAddress) {
      blockers.push({
        code: 'NO_DELIVERY',
        message: 'Selecione um endereço de entrega',
      });
    }

    if (input.hasFiscalContext === false)
      blockers.push({
        code: 'NO_FISCAL_CONTEXT',
        message: 'Selecione um endereço do cliente como referência fiscal na etapa Entrega.',
      });

    const hasSimItemsWithoutConnectivity = input.cartItems.some(
      (item) =>
        (item.negotiationType === NegotiationType.SALE_WITH_SIM_CARD ||
          item.negotiationType === NegotiationType.SIM_CARD) &&
        !item.connectivityDetails,
    );
    if (hasSimItemsWithoutConnectivity) {
      blockers.push({
        code: 'SIM_NO_CONNECTIVITY',
        message: 'Itens com SIM Card precisam ter conectividade configurada',
      });
    }

    if (!input.creditAnalysisResolution?.isValid) {
      blockers.push({
        code: 'CREDIT_ANALYSIS_PENDING',
        message:
          input.creditAnalysisResolution?.message ??
          'Não foi possível validar a análise de crédito desta proposta.',
      });
    }

    return {
      canSubmit: blockers.length === 0,
      blockers,
    };
  }, [
    input.proposalStatus,
    input.hasCartItems,
    input.hasPayment,
    input.hasDeliveryAddress,
    input.hasFiscalContext,
    input.cartItems,
    input.creditAnalysisResolution,
  ]);
}
